import type { ReactNode } from 'react';
import { MoreVertical } from 'lucide-react';
import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';

/**
 * Las acciones de una fila, en un menú.
 *
 * 🔴 Antes eran botones de ícono sueltos, uno al lado del otro, **sin nombre
 * accesible**: un lector de pantalla leía "botón, botón, botón" y no había forma
 * de saber a qué fila pertenecían. Algunos tenían `title`, que no se anuncia de
 * forma confiable; otros no tenían nada.
 *
 * Además no escalaba: con cuatro acciones por fila la columna se volvía una
 * hilera de íconos indistinguibles, y agregar una quinta empeoraba la tabla.
 *
 * El menú resuelve las dos cosas: cada acción tiene texto, y el disparador dice
 * de qué fila es.
 */

export interface AccionDeFila {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  /** Se pinta en rojo. Para lo que corta accesos o borra. */
  destructive?: boolean;
  disabled?: boolean;
  /** Dibuja una línea antes de esta acción, para separar lo peligroso. */
  separadorAntes?: boolean;
}

interface RowActionsMenuProps {
  /**
   * A qué fila pertenece, para el nombre accesible: "Acciones de Ana Gutiérrez".
   * Sin esto el menú vuelve a ser un ícono anónimo.
   */
  descripcion: string;
  acciones: AccionDeFila[];
}

export function RowActionsMenu({ descripcion, acciones }: RowActionsMenuProps) {
  const visibles = acciones.filter(Boolean);
  if (visibles.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label={`Acciones de ${descripcion}`}
        >
          <MoreVertical className="h-4 w-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        {visibles.map((accion, i) => (
          <div key={`${accion.label}-${i}`}>
            {accion.separadorAntes && i > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onSelect={accion.onSelect}
              disabled={accion.disabled}
              className={accion.destructive ? 'text-destructive focus:text-destructive' : undefined}
            >
              {accion.icon && (
                <span className="mr-2 flex h-4 w-4 items-center" aria-hidden="true">
                  {accion.icon}
                </span>
              )}
              {accion.label}
            </DropdownMenuItem>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default RowActionsMenu;
